import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ColorPreference, useColorMode } from "src/design/ColorModeProvider";
import { elevation, radius } from "src/design/tokens";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { useLogoutServerMutation } from "src/store/reducers/auth/authApiSlice";
import {
  logOut,
  selectCurrentRefreshToken,
  selectCurrentScopes,
  selectCurrentUser,
} from "src/store/reducers/auth/authSlice";

const NEXT_PREFERENCE: Record<ColorPreference, ColorPreference> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const humanize = (role: string) => {
  const words = role.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const AccountMenu = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { preference, setPreference } = useColorMode();
  const { hasPermissions } = usePermissions();
  const user = useAppSelector(selectCurrentUser);
  const scopes = useAppSelector(selectCurrentScopes);
  const refreshToken = useAppSelector(selectCurrentRefreshToken);
  const [logoutServer] = useLogoutServerMutation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const fullName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const name = fullName || user?.email || "";
  const initials = (
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`
      : (user?.email ?? "").slice(0, 2)
  ).toUpperCase();

  const role = user?.roles?.[0]
    ? t(`roles.${user.roles[0]}`, { defaultValue: humanize(user.roles[0]) })
    : "";
  const school =
    user?.organization?.type === "school" ? user.organization.name : "";
  const roleLine = [role, school].filter(Boolean).join(" · ");

  const districtId = scopes?.districtId ?? user?.scopes?.districtId;
  const canManagePrivacy =
    Boolean(districtId) &&
    hasPermissions(
      permissions.MANAGE_PRIVACY.action,
      permissions.MANAGE_PRIVACY.subject,
    );

  const close = () => setAnchor(null);
  const go = (path: string) => {
    close();
    history.push(path);
  };

  const onLogout = async () => {
    close();
    try {
      await logoutServer({ refreshToken }).unwrap();
    } catch {
      /* the server session may already be gone; clear local state regardless */
    }
    dispatch(logOut());
    window.location.replace("/grading");
  };

  const itemSx = {
    borderRadius: `${radius.menuItem}px`,
    fontSize: 13,
    fontWeight: 600,
    minHeight: 40,
  };

  return (
    <>
      <IconButton
        aria-label={t("nav.accountMenu")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={(theme) => ({
          width: 38,
          height: 38,
          fontSize: 13,
          fontWeight: 800,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": { bgcolor: theme.palette.primary.main, opacity: 0.85 },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: -5,
          },
        })}
      >
        {initials}
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 260,
              borderRadius: `${radius.panel}px`,
              boxShadow: elevation.menu,
              mt: 1,
            },
          },
        }}
        MenuListProps={{ sx: { p: 1 } }}
      >
        <Box
          sx={{
            px: 1.5,
            pt: 1,
            pb: 1.5,
            mb: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ fontSize: 14, fontWeight: 800 }}>{name}</Box>
          {roleLine && (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              {roleLine}
            </Box>
          )}
        </Box>
        <MenuItem sx={itemSx} onClick={() => go("/grading/profile")}>
          {t("nav.profile")}
        </MenuItem>
        {canManagePrivacy && (
          <MenuItem
            sx={itemSx}
            onClick={() => go(`/grading/districts/${districtId}/privacy`)}
          >
            {t("nav.privacySettings", { defaultValue: "Privacy settings" })}
          </MenuItem>
        )}
        <MenuItem
          sx={itemSx}
          onClick={() => setPreference(NEXT_PREFERENCE[preference])}
        >
          {t("theme.appearance")} · {t(`theme.${preference}`)}
        </MenuItem>
        <MenuItem
          sx={{ ...itemSx, color: (theme) => theme.palette.dangerInk }}
          onClick={onLogout}
        >
          {t("nav.signOut")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default AccountMenu;
